import React, { useState, FormEvent } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from 'lucide-react';

interface ReauthDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirm: (password: string) => Promise<void>;
    isProcessing: boolean;
}

export const ReauthDialog: React.FC<ReauthDialogProps> = ({ isOpen, onOpenChange, onConfirm, isProcessing }) => {
    const [password, setPassword] = useState('');

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (!password) return;
        await onConfirm(password);
        setPassword('');
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="rounded-2xl border-slate-200 bg-white">
                <DialogHeader>
                    <DialogTitle className="tracking-[-0.02em] text-slate-950">Re-authenticate to Continue</DialogTitle>
                    <DialogDescription className="text-slate-600">For your security, please enter your password to confirm account deletion.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 pt-2">
                    <div>
                        <Label htmlFor="reauth-password">Password</Label>
                        <Input 
                            id="reauth-password" 
                            type="password" 
                            value={password} 
                            onChange={(e) => setPassword(e.target.value)} 
                            required 
                            disabled={isProcessing} 
                            autoFocus
                            className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
                        />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" className="rounded-xl border-slate-200 bg-white hover:bg-slate-50" onClick={() => onOpenChange(false)} disabled={isProcessing}>Cancel</Button>
                        <Button type="submit" variant="destructive" className="rounded-xl" disabled={isProcessing || !password}>
                            {isProcessing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Confirm Deletion
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
